import { createHttpService } from "../ApiService";
import {
  TAssessmentFramework,
  TCreateAssessmentFrameworkRequest,
  TUpdateAssessmentFrameworkRequest,
  TCreateAcademicAssessmentRequest,
  TUpdateAcademicAssessmentRequest,
  TAcademicAssessmentsClassesWireResponse,
  TAcademicAssessmentsClassesQuery,
  TGetAcademicAssessmentResponse,
  TAcademicAssessmentListQuery,
  TPaginatedAcademicAssessments,
  TScoringStructure,
  TCreateScoringStructureRequest,
  TUpdateScoringStructureRequest,
  TGradeCriteria,
  TCreateGradeCriteriaRequest,
  TUpdateGradeCriteriaRequest,
  TEvaluationFramework,
  TCreateEvaluationFrameworkRequest,
  TUpdateEvaluationFrameworkRequest,
} from "./assessment.interfaces";
import { mergePaginationQuery } from "../common.interfaces";
import {
  assessmentListMock,
  assessmentFrameworkListMock,
  scoringStructureListMock,
  gradeCriteriaListMock,
  evaluationFrameworkListMock,
} from "../../__mocks__/assessment/assessmentMock";
import {
  classListMock,
  courseListMock,
  gradeLevelListMock,
} from "../../__mocks__/academic/academicMock";

/** List responses may be a bare array or wrapped (`{ data: [...] }`, sometimes nested). */
const coerceList = <T>(raw: unknown): T[] => {
  const peelData = (value: unknown): unknown => {
    if (
      value !== null &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      "data" in value
    ) {
      return (value as { data: unknown }).data;
    }
    return value;
  };

  let v: unknown = peelData(raw);
  if (
    v !== null &&
    typeof v === "object" &&
    !Array.isArray(v) &&
    "data" in v
  ) {
    v = peelData(v);
  }

  return Array.isArray(v) ? (v as T[]) : [];
};

export const useAssessmentService = () => {
  const httpAPI = createHttpService();
  const baseAssessmentPath = "";

  // Use mocks when VITE_APP_WITH_MOCK_DATA is set to "true"
  const appWithMockData = import.meta.env.VITE_APP_WITH_MOCK_DATA === "true";
  const shouldUseMock = appWithMockData;

  const getClassName = (classId: string) =>
    classListMock.find((c) => c.id === classId)?.name;
  const getGradeLevelName = (gradeLevelId: string) =>
    gradeLevelListMock.find((g) => g.id === gradeLevelId)?.name;


  /** OpenAPI: `GET/POST /v1/academics/assessments`, `GET …/assessments/:id` */
  const getAcademicAssessments = (query?: TAcademicAssessmentListQuery) => {
    if (shouldUseMock) {
      let list = [...assessmentListMock];
      if (query?.instituteId) {
        list = list.filter((a) => {
          const iid = (a.institute as { id?: string } | undefined)?.id;
          return !iid || iid === query.instituteId;
        });
      }
      if (query?.classId) {
        list = list.filter(
          (a) => (a.class as { id?: string } | undefined)?.id === query.classId,
        );
      }
      if (query?.courseId) {
        list = list.filter(
          (a) => (a.course as { id?: string } | undefined)?.id === query.courseId,
        );
      }
      if (query?.status) {
        list = list.filter((a) => a.status === query.status);
      }
      if (query?.assessmentType) {
        list = list.filter((a) => a.assessmentType === query.assessmentType);
      }
      return Promise.resolve({ data: list, count: list.length });
    }
    const pagination = mergePaginationQuery({
      page: query?.page,
      limit: query?.limit,
    });
    const params: Record<string, string | number> = {
      page: pagination.page,
      limit: pagination.limit,
    };
    if (query?.instituteId) params.instituteId = query.instituteId;
    if (query?.classId) params.classId = query.classId;
    if (query?.courseId) params.courseId = query.courseId;
    if (query?.academicYear) params.academicYear = query.academicYear;
    if (query?.assessmentType) params.assessmentType = query.assessmentType;
    if (query?.status) params.status = query.status;

    return httpAPI.academicsRoot.get<TPaginatedAcademicAssessments>(
      `/assessments`,
      { params },
    );
  };

  const createAcademicAssessment = (
    request: TCreateAcademicAssessmentRequest,
  ) => {
    if (shouldUseMock) {
      const row: TGetAcademicAssessmentResponse = {
        id: `assessment-${Date.now()}`,
        name: request.name,
        description: request.description,
        assessmentType: request.assessmentType,
        status: request.status ?? "draft",
        maxScore: request.maxScore,
        academicYear: request.academicYear,
        instructions: request.instructions,
        weightage: request.weightage,
        dueDate: request.dueDate,
        scheduledDate: request.scheduledDate,
        class: request.classId
          ? {
              id: request.classId,
              name: getClassName(request.classId) ?? "",
            }
          : undefined,
        course: request.courseId
          ? {
              id: request.courseId,
              name:
                courseListMock.find((c) => c.id === request.courseId)?.name ??
                "",
            }
          : undefined,
        institute: { id: request.instituteId },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      assessmentListMock.push(row);
      return Promise.resolve(row);
    }
    return httpAPI.academicsRoot.post<TGetAcademicAssessmentResponse>(
      `/assessments`,
      request,
    );
  };

  const getAcademicAssessmentById = (id: string) => {
    if (shouldUseMock) {
      const found = assessmentListMock.find((a) => a.id === id);
      if (!found) {
        return Promise.reject(new Error("Assessment not found"));
      }
      return Promise.resolve(found);
    }
    return httpAPI.academicsRoot.get<TGetAcademicAssessmentResponse>(
      `/assessments/${id}`,
    );
  };

  /** OpenAPI does not document PATCH yet; live BE may support partial update on this path. */
  const updateAcademicAssessment = (
    id: string,
    request: TUpdateAcademicAssessmentRequest,
  ) => {
    if (shouldUseMock) {
      const index = assessmentListMock.findIndex((a) => a.id === id);
      if (index === -1) {
        return Promise.reject(new Error("Assessment not found"));
      }
      const prev = assessmentListMock[index];
      const next: TGetAcademicAssessmentResponse = {
        ...prev,
        updatedAt: new Date().toISOString(),
      };
      if (request.name !== undefined) next.name = request.name;
      if (request.description !== undefined) next.description = request.description;
      if (request.assessmentType !== undefined)
        next.assessmentType = request.assessmentType;
      if (request.status !== undefined) next.status = request.status;
      if (request.maxScore !== undefined) next.maxScore = request.maxScore;
      if (request.academicYear !== undefined)
        next.academicYear = request.academicYear;
      if (request.instructions !== undefined)
        next.instructions = request.instructions;
      if (request.scheduledDate !== undefined)
        next.scheduledDate = request.scheduledDate;
      if (request.dueDate !== undefined) next.dueDate = request.dueDate;
      if (request.weightage !== undefined) next.weightage = request.weightage;
      if (request.classId !== undefined) {
        next.class = request.classId
          ? {
              id: request.classId,
              name: getClassName(request.classId) ?? "",
            }
          : undefined;
      }
      if (request.courseId !== undefined) {
        next.course = request.courseId
          ? {
              id: request.courseId,
              name:
                courseListMock.find((c) => c.id === request.courseId)?.name ??
                "",
            }
          : undefined;
      }
      assessmentListMock[index] = next;
      return Promise.resolve(next);
    }
    return httpAPI.academicsRoot.patch<TGetAcademicAssessmentResponse>(
      `/assessments/${id}`,
      request,
    );
  };

  /** GET `/assessments/classes`; response schema omitted in OpenAPI — typed as open object. */
  const getAcademicAssessmentsClasses = (
    query?: TAcademicAssessmentsClassesQuery,
  ) => {
    if (shouldUseMock) {
      return Promise.resolve({} as TAcademicAssessmentsClassesWireResponse);
    }
    const params: Record<string, string> = {};
    if (query?.instituteId) params.instituteId = query.instituteId;
    if (query?.academicYear) params.academicYear = query.academicYear;
    if (query?.assessmentType) params.assessmentType = query.assessmentType;
    if (query?.status) params.status = query.status;
    return httpAPI.academicsRoot.get<TAcademicAssessmentsClassesWireResponse>(
      `/assessments/classes`,
      { params },
    );
  };

  /** Not in `docs/api-reference/openapi-spec.json`: there is no `/v1/assessment` tree.
   *  Real backends return 404 unless this module is implemented separately. Use
   *  `VITE_APP_WITH_MOCK_DATA=true` or the **Assessments** tab (`/v1/academics/assessments`). */
  const getAssessmentFrameworks = (instituteId?: string) => {
    if (shouldUseMock) {
      if (instituteId) {
        return Promise.resolve(
          assessmentFrameworkListMock.filter(
            (framework) => framework.instituteId === instituteId,
          ),
        );
      }
      return Promise.resolve(assessmentFrameworkListMock);
    }
    const path = instituteId
      ? `${baseAssessmentPath}/frameworks?instituteId=${instituteId}`
      : `${baseAssessmentPath}/frameworks`;
    return httpAPI.assessment.get<TAssessmentFramework[]>(path);
  };

  const getAssessmentFrameworkById = (id: string) => {
    if (shouldUseMock) {
      const framework = assessmentFrameworkListMock.find((f) => f.id === id);
      if (!framework) {
        return Promise.reject(new Error("Assessment framework not found"));
      }
      return Promise.resolve(framework);
    }
    return httpAPI.assessment.get<TAssessmentFramework>(
      `${baseAssessmentPath}/frameworks/${id}`,
    );
  };

  const createAssessmentFramework = (
    request: TCreateAssessmentFrameworkRequest,
  ) => {
    if (shouldUseMock) {
      const newFramework: TAssessmentFramework = {
        id: `framework-${Date.now()}`,
        name: request.name,
        classId: request.classId,
        className: getClassName(request.classId),
        assessmentType: request.assessmentType,
        description: request.description,
        status: request.status || "active",
        instituteId: request.instituteId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      assessmentFrameworkListMock.push(newFramework);
      return Promise.resolve(newFramework);
    }
    return httpAPI.assessment.post<TAssessmentFramework>(
      `${baseAssessmentPath}/frameworks`,
      request,
    );
  };

  const updateAssessmentFramework = (
    id: string,
    request: TUpdateAssessmentFrameworkRequest,
  ) => {
    if (shouldUseMock) {
      const index = assessmentFrameworkListMock.findIndex(
        (framework) => framework.id === id,
      );
      if (index === -1) {
        return Promise.reject(new Error("Assessment framework not found"));
      }

      const updatedFramework: TAssessmentFramework = {
        ...assessmentFrameworkListMock[index],
        ...request,
        className:
          request.classId !== undefined
            ? getClassName(request.classId)
            : assessmentFrameworkListMock[index].className,
        updatedAt: new Date().toISOString(),
      };
      assessmentFrameworkListMock[index] = updatedFramework;
      return Promise.resolve(updatedFramework);
    }
    return httpAPI.assessment.patch<TAssessmentFramework>(
      `${baseAssessmentPath}/frameworks/${id}`,
      request,
    );
  };

  const deleteAssessmentFramework = (id: string) => {
    if (shouldUseMock) {
      const index = assessmentFrameworkListMock.findIndex(
        (framework) => framework.id === id,
      );
      if (index === -1) {
        return Promise.reject(new Error("Assessment framework not found"));
      }
      assessmentFrameworkListMock.splice(index, 1);
      return Promise.resolve(void 0);
    }
    return httpAPI.assessment.delete<void>(
      `${baseAssessmentPath}/frameworks/${id}`,
    );
  };

  // Story 7.3: Scoring Structure APIs
  const getScoringStructures = (instituteId?: string, classId?: string) => {
    if (shouldUseMock) {
      let filtered = scoringStructureListMock;
      if (instituteId) {
        filtered = filtered.filter(
          (structure) => structure.instituteId === instituteId,
        );
      }
      if (classId) {
        filtered = filtered.filter(
          (structure) => structure.classId === classId,
        );
      }
      return Promise.resolve(filtered);
    }
    const params = new URLSearchParams();
    if (instituteId) params.append("instituteId", instituteId);
    if (classId) params.append("classId", classId);
    const query = params.toString() ? `?${params.toString()}` : "";
    return httpAPI.assessment
      .get<unknown>(`${baseAssessmentPath}/scoring-structures${query}`)
      .then(coerceList<TScoringStructure>);
  };

  const getScoringStructureById = (id: string) => {
    if (shouldUseMock) {
      const structure = scoringStructureListMock.find((s) => s.id === id);
      if (!structure) {
        return Promise.reject(new Error("Scoring structure not found"));
      }
      return Promise.resolve(structure);
    }
    return httpAPI.assessment.get<TScoringStructure>(
      `${baseAssessmentPath}/scoring-structures/${id}`,
    );
  };

  const createScoringStructure = (request: TCreateScoringStructureRequest) => {
    if (shouldUseMock) {
      const newStructure: TScoringStructure = {
        id: `scoring-${Date.now()}`,
        classId: request.classId,
        className: getClassName(request.classId),
        frameworkId: request.frameworkId,
        totalMarks: request.totalMarks,
        passingMarks: request.passingMarks,
        components: request.components.map((comp, idx) => ({
          id: `comp-${Date.now()}-${idx}`,
          ...comp,
        })),
        instituteId: request.instituteId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      scoringStructureListMock.push(newStructure);
      return Promise.resolve(newStructure);
    }
    return httpAPI.assessment.post<TScoringStructure>(
      `${baseAssessmentPath}/scoring-structures`,
      request,
    );
  };

  const updateScoringStructure = (
    id: string,
    request: TUpdateScoringStructureRequest,
  ) => {
    if (shouldUseMock) {
      const index = scoringStructureListMock.findIndex(
        (structure) => structure.id === id,
      );
      if (index === -1) {
        return Promise.reject(new Error("Scoring structure not found"));
      }

      const updatedStructure: TScoringStructure = {
        ...scoringStructureListMock[index],
        ...request,
        components: request.components
          ? request.components.map((comp, idx) => ({
              id:
                scoringStructureListMock[index].components[idx]?.id ||
                `comp-${Date.now()}-${idx}`,
              ...comp,
            }))
          : scoringStructureListMock[index].components,
        updatedAt: new Date().toISOString(),
      };
      scoringStructureListMock[index] = updatedStructure;
      return Promise.resolve(updatedStructure);
    }
    return httpAPI.assessment.patch<TScoringStructure>(
      `${baseAssessmentPath}/scoring-structures/${id}`,
      request,
    );
  };

  const deleteScoringStructure = (id: string) => {
    if (shouldUseMock) {
      const index = scoringStructureListMock.findIndex(
        (structure) => structure.id === id,
      );
      if (index === -1) {
        return Promise.reject(new Error("Scoring structure not found"));
      }
      scoringStructureListMock.splice(index, 1);
      return Promise.resolve(void 0);
    }
    return httpAPI.assessment.delete<void>(
      `${baseAssessmentPath}/scoring-structures/${id}`,
    );
  };

  // Story 7.4: Grade Criteria APIs
  const getGradeCriteria = (instituteId?: string, gradeLevelId?: string) => {
    if (shouldUseMock) {
      let filtered = gradeCriteriaListMock;
      if (instituteId) {
        filtered = filtered.filter(
          (criteria) => criteria.instituteId === instituteId,
        );
      }
      if (gradeLevelId) {
        filtered = filtered.filter(
          (criteria) => criteria.gradeLevelId === gradeLevelId,
        );
      }
      return Promise.resolve(filtered);
    }
    const params = new URLSearchParams();
    if (instituteId) params.append("instituteId", instituteId);
    if (gradeLevelId) params.append("gradeLevelId", gradeLevelId);
    const query = params.toString() ? `?${params.toString()}` : "";
    return httpAPI.assessment
      .get<unknown>(`${baseAssessmentPath}/grade-criteria${query}`)
      .then(coerceList<TGradeCriteria>);
  };

  const getGradeCriteriaById = (id: string) => {
    if (shouldUseMock) {
      const criteria = gradeCriteriaListMock.find((c) => c.id === id);
      if (!criteria) {
        return Promise.reject(new Error("Grade criteria not found"));
      }
      return Promise.resolve(criteria);
    }
    return httpAPI.assessment.get<TGradeCriteria>(
      `${baseAssessmentPath}/grade-criteria/${id}`,
    );
  };

  const createGradeCriteria = (request: TCreateGradeCriteriaRequest) => {
    if (shouldUseMock) {
      const newCriteria: TGradeCriteria = {
        id: `grade-criteria-${Date.now()}`,
        gradeLevelId: request.gradeLevelId,
        gradeLevelName: getGradeLevelName(request.gradeLevelId),
        minMarks: request.minMarks,
        maxMarks: request.maxMarks,
        description: request.description,
        performanceLevel: request.performanceLevel,
        rubrics: request.rubrics?.map((rubric, idx) => ({
          id: `rubric-${Date.now()}-${idx}`,
          ...rubric,
        })),
        instituteId: request.instituteId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      gradeCriteriaListMock.push(newCriteria);
      return Promise.resolve(newCriteria);
    }
    return httpAPI.assessment.post<TGradeCriteria>(
      `${baseAssessmentPath}/grade-criteria`,
      request,
    );
  };

  const updateGradeCriteria = (
    id: string,
    request: TUpdateGradeCriteriaRequest,
  ) => {
    if (shouldUseMock) {
      const index = gradeCriteriaListMock.findIndex(
        (criteria) => criteria.id === id,
      );
      if (index === -1) {
        return Promise.reject(new Error("Grade criteria not found"));
      }

      const updatedCriteria: TGradeCriteria = {
        ...gradeCriteriaListMock[index],
        ...request,
        rubrics: request.rubrics
          ? request.rubrics.map((rubric, idx) => ({
              id:
                gradeCriteriaListMock[index].rubrics?.[idx]?.id ||
                `rubric-${Date.now()}-${idx}`,
              ...rubric,
            }))
          : gradeCriteriaListMock[index].rubrics,
        updatedAt: new Date().toISOString(),
      };
      gradeCriteriaListMock[index] = updatedCriteria;
      return Promise.resolve(updatedCriteria);
    }
    return httpAPI.assessment.patch<TGradeCriteria>(
      `${baseAssessmentPath}/grade-criteria/${id}`,
      request,
    );
  };

  const deleteGradeCriteria = (id: string) => {
    if (shouldUseMock) {
      const index = gradeCriteriaListMock.findIndex(
        (criteria) => criteria.id === id,
      );
      if (index === -1) {
        return Promise.reject(new Error("Grade criteria not found"));
      }
      gradeCriteriaListMock.splice(index, 1);
      return Promise.resolve(void 0);
    }
    return httpAPI.assessment.delete<void>(
      `${baseAssessmentPath}/grade-criteria/${id}`,
    );
  };

  // Story 7.5: Evaluation Framework APIs
  const getEvaluationFrameworks = (instituteId?: string, classId?: string) => {
    if (shouldUseMock) {
      let filtered = evaluationFrameworkListMock;
      if (instituteId) {
        filtered = filtered.filter(
          (framework) => framework.instituteId === instituteId,
        );
      }
      if (classId) {
        filtered = filtered.filter((framework) =>
          framework.classIds.includes(classId),
        );
      }
      return Promise.resolve(filtered);
    }
    const params = new URLSearchParams();
    if (instituteId) params.append("instituteId", instituteId);
    if (classId) params.append("classId", classId);
    const query = params.toString() ? `?${params.toString()}` : "";
    return httpAPI.assessment.get<TEvaluationFramework[]>(
      `${baseAssessmentPath}/evaluation-frameworks${query}`,
    );
  };

  const getEvaluationFrameworkById = (id: string) => {
    if (shouldUseMock) {
      const framework = evaluationFrameworkListMock.find((f) => f.id === id);
      if (!framework) {
        return Promise.reject(new Error("Evaluation framework not found"));
      }
      return Promise.resolve(framework);
    }
    return httpAPI.assessment.get<TEvaluationFramework>(
      `${baseAssessmentPath}/evaluation-frameworks/${id}`,
    );
  };

  const createEvaluationFramework = (
    request: TCreateEvaluationFrameworkRequest,
  ) => {
    if (shouldUseMock) {
      const newFramework: TEvaluationFramework = {
        id: `eval-framework-${Date.now()}`,
        name: request.name,
        classIds: request.classIds,
        className: request.classIds
          .map((id) => getClassName(id))
          .filter((n): n is string => !!n),
        evaluationModel: request.evaluationModel,
        evaluationFrequency: request.evaluationFrequency,
        assessmentComponents: request.assessmentComponents.map((comp, idx) => ({
          id: `comp-${Date.now()}-${idx}`,
          ...comp,
        })),
        gradingMethodology: request.gradingMethodology,
        gradeLevelIds: request.gradeLevelIds,
        gradeLevelNames: request.gradeLevelIds
          .map((id) => getGradeLevelName(id))
          .filter((n): n is string => !!n),
        description: request.description,
        instituteId: request.instituteId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      evaluationFrameworkListMock.push(newFramework);
      return Promise.resolve(newFramework);
    }
    return httpAPI.assessment.post<TEvaluationFramework>(
      `${baseAssessmentPath}/evaluation-frameworks`,
      request,
    );
  };

  const updateEvaluationFramework = (
    id: string,
    request: TUpdateEvaluationFrameworkRequest,
  ) => {
    if (shouldUseMock) {
      const index = evaluationFrameworkListMock.findIndex(
        (framework) => framework.id === id,
      );
      if (index === -1) {
        return Promise.reject(new Error("Evaluation framework not found"));
      }

      const base = evaluationFrameworkListMock[index];
      const classIds = request.classIds ?? base.classIds;
      const gradeLevelIds = request.gradeLevelIds ?? base.gradeLevelIds;
      const updatedFramework: TEvaluationFramework = {
        ...base,
        ...request,
        classIds,
        className: classIds
          .map((id) => getClassName(id))
          .filter((n): n is string => !!n),
        gradeLevelIds,
        gradeLevelNames: gradeLevelIds
          .map((id) => getGradeLevelName(id))
          .filter((n): n is string => !!n),
        assessmentComponents: request.assessmentComponents
          ? request.assessmentComponents.map((comp, idx) => ({
              id:
                base.assessmentComponents[idx]?.id ||
                `comp-${Date.now()}-${idx}`,
              ...comp,
            }))
          : base.assessmentComponents,
        updatedAt: new Date().toISOString(),
      };
      evaluationFrameworkListMock[index] = updatedFramework;
      return Promise.resolve(updatedFramework);
    }
    return httpAPI.assessment.patch<TEvaluationFramework>(
      `${baseAssessmentPath}/evaluation-frameworks/${id}`,
      request,
    );
  };

  const deleteEvaluationFramework = (id: string) => {
    if (shouldUseMock) {
      const index = evaluationFrameworkListMock.findIndex(
        (framework) => framework.id === id,
      );
      if (index === -1) {
        return Promise.reject(new Error("Evaluation framework not found"));
      }
      evaluationFrameworkListMock.splice(index, 1);
      return Promise.resolve(void 0);
    }
    return httpAPI.assessment.delete<void>(
      `${baseAssessmentPath}/evaluation-frameworks/${id}`,
    );
  };

  return {
    getAcademicAssessments,
    createAcademicAssessment,
    getAcademicAssessmentById,
    updateAcademicAssessment,
    getAcademicAssessmentsClasses,
    getAssessmentFrameworks,
    getAssessmentFrameworkById,
    createAssessmentFramework,
    updateAssessmentFramework,
    deleteAssessmentFramework,
    getScoringStructures,
    getScoringStructureById,
    createScoringStructure,
    updateScoringStructure,
    deleteScoringStructure,
    getGradeCriteria,
    getGradeCriteriaById,
    createGradeCriteria,
    updateGradeCriteria,
    deleteGradeCriteria,
    getEvaluationFrameworks,
    getEvaluationFrameworkById,
    createEvaluationFramework,
    updateEvaluationFramework,
    deleteEvaluationFramework,
  };
};
